import { ChevronDown, ChevronRight, Grid2x2Plus, Plus, Lock, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { ComponentType } from '@/types/schemas/workspace'
import { COMPONENT_META, DASHBOARD_TYPES } from './add-panel-menu-constants'
import { requestPanelAdd, usePanelAvailability } from './use-panel-availability'
import { groupByCategory, useCollapsedCategories } from './add-panel-menu-helpers'

interface AddPanelMenuProps {
  onAdd: (componentType: ComponentType) => void
  /** Which component types are available (defaults to dashboard set). */
  availableTypes?: ComponentType[]
  /** Current per-type counts in the layout — disables types at their limit. */
  panelCounts?: Partial<Record<ComponentType, number>>
  /** When true, placement mode is active — show Cancel button instead of dropdown. */
  isPlacementMode?: boolean
  /** Called when user clicks Cancel during placement mode. */
  onCancelPlacement?: () => void
  /** Extra classes for the trigger button — e.g. a header surface color that
   *  reads better than the default outline on a dark sidebar. */
  buttonClassName?: string
  /** Compact trigger: a lone grid-plus glyph (tooltip carries the label). */
  iconOnly?: boolean
  /** Only meaningful with `iconOnly`: keep the compact grid-plus glyph but
   *  render the text label beside it, so the header control reads as a labelled
   *  button instead of a bare icon. The tooltip drops out - the label says it.
   *  Without `iconOnly` the label is always drawn, so this is a no-op there. */
  showLabel?: boolean
}

/**
 * The list of component DropdownMenuItems with plan-gating, shared by the
 * Add Panel button and the per-pane tab strip's "+" menu. Render inside a
 * DropdownMenuContent.
 */
export function AddComponentMenuItems({
  availableTypes = DASHBOARD_TYPES,
  onPick,
  panelCounts,
}: {
  availableTypes?: ComponentType[]
  onPick: (componentType: ComponentType) => void
  /** Current per-type counts in the layout — used to disable at-limit types. */
  panelCounts?: Partial<Record<ComponentType, number>>
}) {
  const { resolve } = usePanelAvailability(panelCounts)
  const { collapsed, toggle } = useCollapsedCategories()

  const renderItem = (type: ComponentType) => {
    const meta = COMPONENT_META[type]
    const Icon = meta.icon
    const availability = resolve(type)

    return (
      <DropdownMenuItem
        key={type}
        // A plan cap or a lock stays clickable so the refusal can sell the
        // upgrade; only the static per-type ceiling disables the item.
        disabled={availability.disabled}
        onClick={() => {
          if (requestPanelAdd(availability)) onPick(type)
        }}
      >
        <Icon className="mr-2 h-4 w-4" />
        {/* Never let a long label ("Price Action Graph") wrap under the
            trailing "1 max" badge: the label keeps one line and the badge
            keeps its own width, and the menu is sized to hold both. */}
        <span className="whitespace-nowrap">{meta.label}</span>
        {availability.badgeText && (
          <span className="ml-auto shrink-0 whitespace-nowrap pl-3 text-[11px] text-muted-foreground">
            {availability.badgeText}
          </span>
        )}
        {!availability.atLimit && availability.isLocked && (
          <Lock className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
        )}
      </DropdownMenuItem>
    )
  }

  return (
    <>
      {groupByCategory(availableTypes).flatMap(({ category, types }) => {
        const isCollapsed = category != null && collapsed.has(category)
        return [
          category ? (
            <DropdownMenuLabel key={`cat-${category}`} className="p-0">
              {/* Click to collapse/expand — a plain button (not a menu item) so
                  it toggles without selecting/closing the menu. */}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggle(category)
                }}
                className="flex w-full items-center gap-1 px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
              >
                {isCollapsed ? (
                  <ChevronRight className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
                {category}
              </button>
            </DropdownMenuLabel>
          ) : null,
          ...(isCollapsed ? [] : types.map(renderItem)),
        ]
      })}
    </>
  )
}

export function AddPanelMenu({
  onAdd,
  availableTypes = DASHBOARD_TYPES,
  panelCounts,
  isPlacementMode = false,
  onCancelPlacement,
  buttonClassName,
  iconOnly = false,
  showLabel = false,
}: AddPanelMenuProps) {
  // A bare glyph needs the tooltip to name itself; a labelled one does not.
  const labelled = !iconOnly || showLabel

  if (isPlacementMode) {
    return (
      <Button
        variant="outline"
        size="sm"
        className={cn('gap-1', buttonClassName)}
        onClick={onCancelPlacement}
        title={labelled ? undefined : 'Cancel placement'}
      >
        <X className="h-4 w-4" />
        {labelled && 'Cancel'}
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-companion-label="add-panel"
          variant="outline"
          size="sm"
          className={cn('gap-1', buttonClassName)}
          title={labelled ? undefined : 'Add Panel'}
        >
          {iconOnly ? (
            <>
              {/* Rotated so the + lands at the top-right corner. */}
              <Grid2x2Plus className="h-4 w-4 -rotate-90" />
              {showLabel && 'Add Panel'}
            </>
          ) : (
            <>
              <Plus className="h-4 w-4" />
              Add Panel
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[250px]">
        <AddComponentMenuItems
          availableTypes={availableTypes}
          onPick={onAdd}
          panelCounts={panelCounts}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * The header's Add Panel control when the visual Panel Hub owns the choice:
 * a toggle for the rail, or the same Cancel button as the menu while a
 * placement gesture is pending.
 */
export function AddPanelButton({
  isPlacementMode,
  onCancelPlacement,
  isOpen,
  onToggle,
}: {
  isPlacementMode: boolean
  onCancelPlacement: () => void
  isOpen: boolean
  onToggle: () => void
}) {
  const className =
    'h-7 gap-1.5 rounded border-none px-2 text-[11.5px] shadow-none hover:bg-hover hover:text-foreground'
  if (isPlacementMode) {
    return (
      <Button
        variant="outline"
        size="sm"
        className={cn(className, 'bg-transparent text-muted-foreground')}
        onClick={onCancelPlacement}
      >
        <X className="h-4 w-4" />
        Cancel
      </Button>
    )
  }
  return (
    <Button
      data-companion-label="add-panel"
      variant="outline"
      size="sm"
      aria-pressed={isOpen}
      className={cn(
        className,
        isOpen ? 'bg-hover text-foreground' : 'bg-transparent text-muted-foreground',
      )}
      onClick={onToggle}
    >
      {/* Rotated so the + lands at the top-right corner. */}
      <Grid2x2Plus className="h-4 w-4 -rotate-90" />
      Add Panel
    </Button>
  )
}
